import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { AZURE_MJ } from "@/lib/axios";

const SUBCONTRACTOR_KEYS = {
  getSubcontractorTrades: "BC_GET_SUBCONTRACTOR_TRADES",
  getSubcontractors: "BC_GET_SUBCONTRACTORS"
};

export interface ISubcontractorTrade {
  code: string;
  description: string;
}

/** The fields this app reads from BC's (much larger) subcontractor record. */
export interface ISubcontractor {
  no: string;
  name: string;
  tradeCode: string;
  tradeDescription: string;
  /** `""`, `"Payment"`, or `"All"`; only `"All"` rules a subcontractor out. */
  blocked: string;
}

const isFullyBlocked = (subcontractor: ISubcontractor) =>
  subcontractor.blocked?.toLowerCase() === "all";

export const useGetSubcontractorTrades = () =>
  useQuery({
    queryKey: [SUBCONTRACTOR_KEYS.getSubcontractorTrades],
    queryFn: async (): Promise<ISubcontractorTrade[]> => {
      const { data } = await AZURE_MJ.get("/bc/subcontractorTrades");
      return data.value;
    }
  });

export const useGetSubcontractors = () =>
  useQuery({
    queryKey: [SUBCONTRACTOR_KEYS.getSubcontractors],
    queryFn: async (): Promise<ISubcontractor[]> => {
      const { data } = await AZURE_MJ.get("/bc/subcontractors");
      return (data.value as ISubcontractor[]).filter(
        (subcontractor) => !isFullyBlocked(subcontractor)
      );
    }
  });

/**
 * Forecasts save only the subcontractor `no`, so the name is looked up from BC
 * when shown. Falls back to the `no` for one BC no longer returns (e.g. blocked).
 */
export const useSubcontractorName = () => {
  const { data: subcontractors = [] } = useGetSubcontractors();
  const namesByNo = useMemo(
    () => new Map(subcontractors.map(({ no, name }) => [no, name])),
    [subcontractors]
  );
  return useCallback((no: string) => namesByNo.get(no) ?? no, [namesByNo]);
};
